import axios, {
  AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios';
import { useAuthStore } from '../../application/stores/authStore';

/**
 * W03 — apiClient: axios instance with in-memory token attach and a SINGLE-FLIGHT
 * 401 → POST /auth/refresh → retry → logout fallback queue (Key Invariant 2).
 *
 * Queue semantics: concurrent 401s share ONE refresh promise; each queued config is
 * retried exactly once after refresh; if refresh fails, every queued request rejects
 * and the session is cleared exactly once.
 */
export const apiClient: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '',
  withCredentials: true, // refresh token is an httpOnly cookie
  timeout: 15_000,
  headers: { 'Content-Type': 'application/json' },
});

// --- Request interceptor: attach in-memory bearer token ---
apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = useAuthStore.getState().accessToken;
  if (token && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // E13 is multipart/form-data: drop the JSON content type so axios passes the FormData
  // through untouched (instead of JSON-stringifying it) and the browser sets the boundary.
  if (config.data instanceof FormData) {
    config.headers.delete?.('Content-Type');
    if ((config.headers as Record<string, unknown>)['Content-Type']) {
      delete (config.headers as Record<string, unknown>)['Content-Type'];
    }
  }
  return config;
});

// --- Single-flight refresh machinery ---
let refreshPromise: Promise<string> | null = null;

async function performRefresh(): Promise<string> {
  // Use a bare axios call to avoid interceptor recursion.
  const { data } = await axios.post(
    (import.meta.env.VITE_API_URL || '') + '/auth/refresh',
    null,
    { withCredentials: true },
  );
  const nextToken: unknown = data?.accessToken;
  if (typeof nextToken !== 'string' || !nextToken) {
    throw new Error('Refresh response missing accessToken');
  }
  useAuthStore.getState().setAccessToken(nextToken);
  return nextToken;
}

function refreshOnce(): Promise<string> {
  if (!refreshPromise) {
    refreshPromise = performRefresh().finally(() => {
      refreshPromise = null; // release so future expiries can refresh again
    });
  }
  return refreshPromise;
}

interface RetriableConfig extends AxiosRequestConfig {
  __retried?: boolean;
}

const AUTH_PATHS = ['/auth/login', '/auth/select-account', '/auth/refresh', '/auth/logout', '/auth/password-reset'];

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const config = error.config as RetriableConfig | undefined;
    const status = error.response?.status;
    const url = config?.url ?? '';

    const isAuthPath = AUTH_PATHS.some((p) => url.includes(p));

    if (status === 401 && config && !config.__retried && !isAuthPath) {
      config.__retried = true;
      try {
        const token = await refreshOnce(); // concurrent 401s await the same promise
        config.headers = config.headers ?? {};
        (config.headers as Record<string, string>).Authorization = `Bearer ${token}`;
        return apiClient(config);
      } catch (refreshError) {
        // Refresh failed → logout fallback: clear session exactly once.
        useAuthStore.getState().clearSession();
        if (import.meta.env.DEV) {
          console.info('[auth] refresh failed — session cleared (logout fallback)');
        }
        return Promise.reject(refreshError instanceof AxiosError ? error : refreshError);
      }
    }

    return Promise.reject(error);
  },
);

export { refreshOnce };
